import {
  findAccount,
  NO_ACCOUNTS,
  publicAccount,
  publicView,
  readSlot,
  slotOf,
  withAccount,
  withActive,
  withoutAccount,
  withRefreshToken,
  type AccountsDocument,
  type StoredAccount,
} from './device-accounts.ts'
import { refreshTokens } from './endpoints.ts'
import { AuthError } from './errors.ts'
import { stringClaim } from './jwt.ts'
import { createSessionStore, toAccessToken, type SessionStore } from './session.ts'
import { MemoryRefreshTokenStore } from './storage.ts'
import type {
  AccountKind,
  AuthConfig,
  DeviceAccount,
  DeviceAccounts,
  RefreshTokenStore,
  TokenResponse,
} from './types.ts'

// What every half of the client shares: one session store, the device's accounts, one refresh in flight
// per account. Only the one in use has tokens in memory: packages/auth/README.md § «Più account sullo stesso telefono».

/** How a sign-in joins the device's accounts. */
export interface SignedInAs {
  readonly kind: AccountKind
  /** False keeps the account in use: a tutore adding a child stays themselves. */
  readonly activate: boolean
  readonly name?: string
  readonly personId?: number
  readonly addedBy?: string
}

const OWN_AND_IN_USE: SignedInAs = { kind: 'own', activate: true }

export interface AuthKernel {
  readonly config: AuthConfig
  readonly store: SessionStore
  accounts(): DeviceAccounts
  subscribe(listener: () => void): () => void
  /** Writes a sign-in into the device's accounts, rotating its stored refresh token. */
  applyTokens(tokens: TokenResponse, as?: SignedInAs): Promise<DeviceAccount>
  /** Puts another account in use with its own refresh token. A spent token takes that account away, and nothing else does. */
  switchTo(sub: string): Promise<void>
  /** Takes an account off this device and hands back what it held, for the caller to revoke. */
  forget(sub: string): Promise<StoredAccount | undefined>
  forgetAll(): Promise<readonly StoredAccount[]>
  /** The account in use, gone from this device; with one account at a time, the slot emptied. With
   *  several and nobody in use, nothing goes. */
  forgetActive(): Promise<void>
  /** A hosted-UI sign-in that failed half-way: no session, and the accounts already here stay. */
  abandonSignIn(): Promise<void>
  /** Forces a refresh of the account in use. Concurrent calls share one request. */
  refresh(): Promise<string | null>
  /** A valid access token, refreshing first when it is missing or about to expire. */
  accessToken(): Promise<string | null>
}

export interface KernelOptions {
  readonly config: AuthConfig
  readonly refreshTokenStore?: RefreshTokenStore
  /** Off by default: a sign-in replaces the account before it, as admin-panel wants. */
  readonly multipleAccounts?: boolean
}

function idTokenOf(tokens: TokenResponse): string {
  if (!tokens.id_token) {
    // The id_token IS the API credential: without it the session authenticates and then
    // 401s on every request, three layers from the cause (usually a missing `openid`).
    throw new AuthError(
      'id_token_missing',
      'The token response carried no id_token — is the openid scope granted?',
    )
  }
  return tokens.id_token
}

function refreshFailure(error: unknown): AuthError {
  return error instanceof AuthError
    ? error
    : new AuthError('refresh_failed', 'The refresh request failed', { cause: error })
}

/** The token is spent, revoked or rotated away, so there is nothing left to retry with. A network
 *  failure or a 5xx is not that: the account stays and the next attempt may work. */
function tokenIsSpent(error: unknown): boolean {
  return error instanceof AuthError && (error.terminal || error.code === 'id_token_missing')
}

export function createKernel(options: KernelOptions): AuthKernel {
  const { config } = options
  const alone = options.multipleAccounts !== true
  const store = createSessionStore()
  const slot = options.refreshTokenStore ?? new MemoryRefreshTokenStore()

  // In use in this tab. The document's `active` is only what a reload brings back: tabs choose apart.
  let activeSub: string | null = null
  let seen: AccountsDocument = NO_ACCOUNTS
  let view: DeviceAccounts = publicView(seen, activeSub)
  const listeners = new Set<() => void>()

  // With rotation on, deduplication is not an optimisation: parallel refreshes of one account would
  // each spend the same token and all but one would be rejected as reuse.
  const inFlight = new Map<string, Promise<TokenResponse>>()
  let inFlightRefresh: Promise<string | null> | null = null

  function publish(document: AccountsDocument = seen): void {
    seen = document
    view = publicView(document, activeSub)
    for (const listener of [...listeners]) listener()
  }

  function inUse(sub: string, tokens: TokenResponse): void {
    activeSub = sub
    store.setAuthenticated({
      accessToken: toAccessToken(tokens.access_token, tokens.expires_in),
      idToken: idTokenOf(tokens),
    })
    publish()
  }

  function noneInUse(): void {
    activeSub = null
    store.reset()
    publish()
  }

  async function loadDocument(): Promise<AccountsDocument> {
    const content = readSlot(await slot.read())
    return content.kind === 'document' ? content.document : NO_ACCOUNTS
  }

  // Read again before every write: another tab may have added an account or rotated a token since.
  async function save(update: (document: AccountsDocument) => AccountsDocument): Promise<void> {
    const next = update(await loadDocument())
    const raw = slotOf(next)
    if (raw === null) await slot.clear()
    else await slot.write(raw)
    publish(next)
  }

  async function applyTokens(
    tokens: TokenResponse,
    as: SignedInAs = OWN_AND_IN_USE,
  ): Promise<DeviceAccount> {
    const idToken = idTokenOf(tokens)
    // Never absent from a Cognito id token; '' keys the one session of a token that is not a JWT.
    const sub = stringClaim(idToken, 'sub') ?? ''
    let written: StoredAccount = { sub, kind: as.kind, email: '', refreshToken: '' }
    await save((document) => {
      const previous = findAccount(document, sub)
      const refreshToken = tokens.refresh_token ?? previous?.refreshToken
      written = {
        sub,
        kind: as.kind,
        email: stringClaim(idToken, 'email') ?? previous?.email ?? '',
        ...(as.name === undefined ? {} : { name: as.name }),
        ...(as.personId === undefined ? {} : { personId: as.personId }),
        ...(as.addedBy === undefined ? {} : { addedBy: as.addedBy }),
        refreshToken: refreshToken ?? '',
      }
      if (refreshToken === undefined) return document
      return withAccount(document, written, { activate: as.activate, alone })
    })
    if (as.activate) inUse(sub, tokens)
    return publicAccount(written)
  }

  async function forget(sub: string): Promise<StoredAccount | undefined> {
    let removed: StoredAccount | undefined
    await save((document) => {
      removed = findAccount(document, sub)
      return withoutAccount(document, sub)
    })
    if (activeSub === sub) noneInUse()
    return removed
  }

  function refreshAccount(held: StoredAccount): Promise<TokenResponse> {
    const running = inFlight.get(held.sub)
    if (running !== undefined) return running
    const started = runRefreshOf(held).finally(() => inFlight.delete(held.sub))
    inFlight.set(held.sub, started)
    return started
  }

  async function runRefreshOf(held: StoredAccount): Promise<TokenResponse> {
    let tokens: TokenResponse
    try {
      tokens = await refreshTokens(config, held.refreshToken)
      idTokenOf(tokens)
    } catch (error) {
      // That account alone goes, and only for a spent token: the others on the device hold tokens of
      // their own, and a phone that is offline must not lose the child it carries.
      if (tokenIsSpent(error)) await forget(held.sub)
      throw refreshFailure(error)
    }
    const rotated = tokens.refresh_token
    // Rotation returns a new one on every refresh; the old one is already dead.
    if (rotated !== undefined)
      await save((document) => withRefreshToken(document, held.sub, rotated))
    return tokens
  }

  /** The single session of before #302: refreshed once, it says whose it is, and becomes that account. */
  async function adoptBareToken(refreshToken: string): Promise<string> {
    try {
      const tokens = await refreshTokens(config, refreshToken)
      await applyTokens({ ...tokens, refresh_token: tokens.refresh_token ?? refreshToken })
      return tokens.access_token
    } catch (error) {
      if (tokenIsSpent(error)) await slot.clear()
      noneInUse()
      throw refreshFailure(error)
    }
  }

  async function runRefresh(): Promise<string | null> {
    const wanted = activeSub
    const content = readSlot(await slot.read())
    if (content.kind === 'bare-token') return adoptBareToken(content.refreshToken)
    publish(content.document)
    const held = findAccount(content.document, wanted ?? content.document.active)
    if (held === undefined) {
      // Not an error: a first visit, an account left on another tab, and an expired one look alike.
      if (activeSub === wanted) noneInUse()
      return null
    }
    let tokens: TokenResponse
    try {
      tokens = await refreshAccount(held)
    } catch (error) {
      if (activeSub === wanted) noneInUse()
      throw error
    }
    // Another account was chosen meanwhile: a request begun as one account is never answered as another.
    if (activeSub !== wanted) return null
    inUse(held.sub, tokens)
    return tokens.access_token
  }

  function refresh(): Promise<string | null> {
    inFlightRefresh ??= runRefresh().finally(() => {
      inFlightRefresh = null
    })
    return inFlightRefresh
  }

  return {
    config,
    store,
    accounts: () => view,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    applyTokens,

    async switchTo(sub) {
      if (activeSub === sub && store.validAccessToken() !== null) return
      const held = findAccount(await loadDocument(), sub)
      if (held === undefined) {
        throw new AuthError('no_refresh_token', 'This device holds no such account')
      }
      inUse(sub, await refreshAccount(held))
      await save((document) => withActive(document, sub))
    },

    forget,

    async forgetAll() {
      const document = await loadDocument()
      await slot.clear()
      activeSub = null
      store.reset()
      publish(NO_ACCOUNTS)
      return document.accounts
    },

    async forgetActive() {
      if (alone) {
        // One account at a time: whatever the slot held goes, a bare token of before included.
        await slot.clear()
        activeSub = null
        store.reset()
        publish(NO_ACCOUNTS)
        return
      }
      // Nobody in use among several: there is nothing to leave, and the accounts are not ours to empty.
      const sub = activeSub ?? (await loadDocument()).active
      if (sub !== null) await forget(sub)
      noneInUse()
    },

    async abandonSignIn() {
      store.reset()
      if (alone) {
        await slot.clear()
        activeSub = null
        publish(NO_ACCOUNTS)
      }
    },

    refresh,
    accessToken: async () => store.validAccessToken() ?? (await refresh()),
  }
}
